// B2316: the Audit log page. Every row is the server's answer to
// `SHOW AUDIT` (web/docs/sql.md § Audit trail), sent on the shared WS.
//
// The server applies the grant filter: a superuser or a holder of the AUDIT
// privilege sees all rows, another user sees only the rows where the user is
// the actor or the target, and the guest gets an error. The page shows that
// error, never a fake empty list.
//
// SECURITY: the actor, the object, the reason, the client name and the other
// text cells come from clients. The page renders each value as a React text
// child, which React escapes. Do not write raw HTML on this page.

import { useCallback, useEffect, useState } from 'react';
import { I } from '../components/icons.jsx';
import { AUDIT_DEFAULT_LIMIT, AUDIT_MAX_LIMIT, AUDIT_RESULTS, listAudit } from '../data/audit-store.js';

function errText(e) { return e?.message || String(e); }

// The columns that the table shows, in the order of the server. `time_us`
// is the same instant as `time`, so the table leaves it out.
const SHOWN = [
  ['time', 'Time (UTC)'], ['node', 'Node'], ['actor', 'Actor'], ['action', 'Action'],
  ['object', 'Object'], ['branch', 'Branch'], ['target', 'Target'], ['grantee', 'Grantee'],
  ['rights', 'Rights'], ['result', 'Result'], ['reason', 'Reason'], ['count', 'Count'],
  ['client_addr', 'Client address'], ['client_name', 'Client name'], ['build', 'Build'],
];

// One cell as text. NULL is a dash. Any other value becomes a string, so a
// value never reaches the markup as an element.
function cellText(v) {
  if (v == null || v === '') return '—';
  return typeof v === 'string' ? v : String(v);
}

// The table of rows, or the error line of the server. `rows` is null before
// the first answer.
export function AuditTable({ rows, loading, error }) {
  return (
    <>
      {error && <div className="users-error" style={{ padding: '12px 16px' }}>{String(error)}</div>}
      <div style={{ overflowX: 'auto' }}>
        <table className="k-table">
          <thead><tr>{SHOWN.map(([k, label]) => <th key={k}>{label}</th>)}</tr></thead>
          <tbody>
            {rows == null && !error && (
              <tr><td colSpan={SHOWN.length} className="users-muted">{loading ? 'Loading…' : ''}</td></tr>
            )}
            {rows != null && rows.length === 0 && !error && (
              <tr><td colSpan={SHOWN.length} className="users-muted">No audit rows match the filters.</td></tr>
            )}
            {!error && (rows ?? []).map((r, i) => (
              <tr key={`${r.time_us ?? ''}-${i}`}>
                {SHOWN.map(([k]) => (
                  <td key={k} className={k === 'count' ? 'num' : k === 'actor' ? 'fg1' : undefined}
                    style={k === 'time' ? { whiteSpace: 'nowrap' } : undefined}>
                    {k === 'result' && r.result === 'refused'
                      ? <span className="pill orange">{cellText(r[k])}</span>
                      : cellText(r[k])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

const EMPTY = { actor: '', target: '', action: '', result: '', node: '', since: '', until: '', limit: String(AUDIT_DEFAULT_LIMIT) };

// The form values as store filters. A `datetime-local` value is a local time
// with no zone, so `new Date` reads it as local time; the store sends it as
// UTC.
function toFilters(form) {
  const t = (s) => s.trim();
  return {
    actor: t(form.actor), target: t(form.target), action: t(form.action),
    result: form.result, node: t(form.node),
    since: form.since ? new Date(form.since) : '',
    until: form.until ? new Date(form.until) : '',
    limit: t(form.limit),
  };
}

export function AuditPage() {
  const [form, setForm] = useState(EMPTY);
  const [state, setState] = useState({ rows: null, error: null, loading: true });

  const load = useCallback(async (f) => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const rows = await listAudit(toFilters(f));
      setState({ rows, error: null, loading: false });
    } catch (e) {
      setState({ rows: null, error: errText(e), loading: false });
    }
  }, []);
  useEffect(() => { load(EMPTY); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const count = state.rows?.length;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="crumbs">Admin · Audit log</div>
          <h1>Audit log</h1>
        </div>
        <div className="actions">
          <button className="btn btn-sm btn-ghost" type="button" onClick={() => load(form)} disabled={state.loading} title="Reload"><I.Refresh size={12} /> Refresh</button>
        </div>
      </div>
      <div className="k-card">
        <div className="k-card-head">
          <span className="title">{count == null ? 'Audit rows' : `${count} row${count === 1 ? '' : 's'}, newest first`}</span>
        </div>
        <form className="users-form" style={{ padding: '10px 16px', borderBottom: '1px solid var(--app-border)' }}
          onSubmit={(e) => { e.preventDefault(); load(form); }}>
          <input placeholder="actor" value={form.actor} onChange={set('actor')} spellCheck={false} />
          <input placeholder="target" value={form.target} onChange={set('target')} spellCheck={false} />
          <input placeholder="action (login, grant_audit, …)" value={form.action} onChange={set('action')} spellCheck={false} />
          <select value={form.result} onChange={set('result')} title="Result">
            <option value="">any result</option>
            {AUDIT_RESULTS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <input placeholder="node" value={form.node} onChange={set('node')} spellCheck={false} style={{ width: 64 }} />
          <span className="users-label" style={{ minWidth: 0 }}>from</span>
          <input type="datetime-local" step="1" value={form.since} onChange={set('since')} title="From (local time)" />
          <span className="users-label" style={{ minWidth: 0 }}>to</span>
          <input type="datetime-local" step="1" value={form.until} onChange={set('until')} title="To (local time)" />
          <span className="users-label" style={{ minWidth: 0 }}>limit</span>
          <input type="number" min={0} max={AUDIT_MAX_LIMIT} value={form.limit} onChange={set('limit')} style={{ width: 80 }} title={`0 to ${AUDIT_MAX_LIMIT}`} />
          <button className="btn btn-primary btn-sm" type="submit" disabled={state.loading}><I.Filter size={12} /> Apply</button>
          <button className="btn btn-ghost btn-sm" type="button" disabled={state.loading}
            onClick={() => { setForm(EMPTY); load(EMPTY); }}>Clear</button>
        </form>
        <AuditTable rows={state.rows} loading={state.loading} error={state.error} />
      </div>
    </div>
  );
}
